// PRD 05 §3.3 — read-only answer display for the 8 answer types. Each type
// has a dedicated subview; the dispatcher at the bottom of the file picks
// based on `q.answerType`.
//
// Edit-mode counterparts live in `AnswerEditors/*` (built in S3).

import { useTranslation } from 'react-i18next';
import { Camera, SlidersHorizontal, TextCursorInput, Type } from 'lucide-react';

import { BranchChip } from './BranchChip';
import type {
  BatteryConfig,
  Choice,
  PhotoConfig,
  Question,
  SliderConfig,
  TextConfig,
} from '../types/questionnaire-design.types';

interface ChoiceTableProps {
  choices: Choice[];
  showBranch: boolean;
  showCorrect: boolean;
  // Maps qid → display number ("N" or "N-M") for any JUMP branch.
  jumpDisplayNumByQid: Map<string, string>;
  isMultiChoice?: boolean;
}

function ChoiceText({ choice }: { choice: Choice }) {
  const { t } = useTranslation('questionnaire-design');
  if (choice.chType === 'other') {
    return (
      <span style={{ color: 'var(--info)', fontWeight: 500 }}>
        <TextCursorInput
          size={12}
          style={{ verticalAlign: 'middle', marginRight: 4 }}
        />
        {t('answerDisplay.otherLabel')}{' '}
        <span
          style={{
            color: 'var(--muted-foreground)',
            fontWeight: 400,
            fontSize: 11,
            marginLeft: 6,
          }}
        >
          {t('answerDisplay.otherHint')}
        </span>
      </span>
    );
  }
  return <>{choice.text}</>;
}

function ChoiceTable({
  choices,
  showBranch,
  showCorrect,
  jumpDisplayNumByQid,
  isMultiChoice,
}: ChoiceTableProps) {
  const { t } = useTranslation('questionnaire-design');
  return (
    <table className="table" style={{ margin: 0, fontSize: 'var(--font-size-xs)' }}>
      <thead>
        <tr>
          <th style={{ padding: 'var(--space-2) var(--space-3)' }}>
            {t('answerDisplay.columns.text')}
          </th>
          {showCorrect ? (
            <th
              style={{
                width: 50,
                textAlign: 'center',
                padding: 'var(--space-2) var(--space-3)',
              }}
            >
              {t('answerDisplay.columns.correct')}
            </th>
          ) : null}
          <th
            style={{
              width: 60,
              textAlign: 'right',
              padding: 'var(--space-2) var(--space-3)',
            }}
          >
            {t('answerDisplay.columns.score')}
          </th>
          {showBranch ? (
            <th style={{ width: 160, padding: 'var(--space-2) var(--space-3)' }}>
              {t('answerDisplay.columns.branch')}
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {isMultiChoice ? (
          <tr>
            <td
              colSpan={2}
              style={{
                padding: 'var(--space-1) var(--space-3)',
                fontSize: 10,
                color: 'var(--info)',
                borderBottom: 'none',
              }}
            >
              {t('answerDisplay.multiHint')}
            </td>
          </tr>
        ) : null}
        {choices.map((choice, idx) => (
          <tr key={idx}>
            <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
              {!showCorrect && !isMultiChoice ? (
                <span style={{ color: 'var(--muted-foreground)', marginRight: 4 }}>
                  ○
                </span>
              ) : null}
              {isMultiChoice ? (
                <span style={{ color: 'var(--muted-foreground)', marginRight: 4 }}>
                  ☐
                </span>
              ) : null}
              <ChoiceText choice={choice} />
            </td>
            {showCorrect ? (
              <td
                style={{
                  textAlign: 'center',
                  padding: 'var(--space-2) var(--space-3)',
                }}
              >
                {choice.correct ? '✔' : ''}
              </td>
            ) : null}
            <td
              style={{
                textAlign: 'right',
                padding: 'var(--space-2) var(--space-3)',
              }}
            >
              {choice.score}
              {t('answerDisplay.scoreSuffix')}
            </td>
            {showBranch ? (
              <td style={{ padding: 'var(--space-2) var(--space-3)' }}>
                <BranchChip
                  branch={choice.branch}
                  jumpDisplayNum={
                    choice.branch?.kind === 'JUMP'
                      ? jumpDisplayNumByQid.get(choice.branch.targetQid ?? '') ?? null
                      : null
                  }
                />
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TextConfigDisplay({ cfg }: { cfg: TextConfig }) {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div
      style={{
        border: '1px dashed var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--space-3)',
        color: 'var(--muted-foreground)',
        fontSize: 'var(--font-size-xs)',
      }}
    >
      <Type size={14} style={{ verticalAlign: 'middle' }} />{' '}
      {t('answerDisplay.textSummary', {
        min: cfg.minChars,
        max: cfg.maxChars,
        score: cfg.score,
      })}
    </div>
  );
}

function SliderConfigDisplay({ cfg }: { cfg: SliderConfig }) {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div
      style={{
        border: '1px dashed var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--space-3)',
        color: 'var(--muted-foreground)',
        fontSize: 'var(--font-size-xs)',
      }}
    >
      <SlidersHorizontal size={14} style={{ verticalAlign: 'middle' }} />{' '}
      {t('answerDisplay.sliderSummary', {
        min: cfg.min,
        max: cfg.max,
        step: cfg.step,
        unit: cfg.unit,
      })}
    </div>
  );
}

function PhotoConfigDisplay({ cfg }: { cfg: PhotoConfig }) {
  const { t } = useTranslation('questionnaire-design');
  return (
    <div
      style={{
        border: '1px dashed var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--space-3)',
        color: 'var(--muted-foreground)',
        fontSize: 'var(--font-size-xs)',
      }}
    >
      <Camera size={14} style={{ verticalAlign: 'middle' }} />{' '}
      {t('answerDisplay.photoSummary', {
        files: cfg.maxFiles,
        size: cfg.maxSizeMB,
      })}
    </div>
  );
}

function BatteryDisplay({ cfg }: { cfg: BatteryConfig }) {
  const { t } = useTranslation('questionnaire-design');
  const totalItems = cfg.categories.reduce((sum, c) => sum + c.items.length, 0);
  return (
    <div>
      {cfg.categories.map((cat, catIdx) => {
        // Items are numbered globally (1..totalItems) across the whole battery.
        let runningNum = cfg.categories
          .slice(0, catIdx)
          .reduce((sum, c) => sum + c.items.length, 0);
        return (
          <div
            key={`${catIdx}-${cat.name}`}
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-3)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--accent)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                color: 'var(--foreground)',
              }}
            >
              {cat.name}
            </div>
            {cat.items.map((item, itemIdx) => {
              runningNum += 1;
              return (
                <div
                  key={`${catIdx}-${itemIdx}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    padding: 'var(--space-2) var(--space-3)',
                    borderTop: '1px solid var(--border)',
                    fontSize: 'var(--font-size-xs)',
                  }}
                >
                  <span
                    style={{
                      width: 20,
                      textAlign: 'center',
                      color: 'var(--muted-foreground)',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {runningNum}
                  </span>
                  <span style={{ flex: 1 }}>
                    {item.text}
                    {item.allowPhoto ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 2,
                          fontSize: 10,
                          padding: '1px 6px',
                          borderRadius: 'var(--radius-sm)',
                          background: '#FEF3C7',
                          color: '#92400E',
                          verticalAlign: 'middle',
                          marginLeft: 6,
                        }}
                      >
                        📷
                      </span>
                    ) : null}
                  </span>
                  <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
                    {cfg.scaleLabels.map((_, idx) => (
                      <span
                        key={idx}
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          border: '1.5px solid var(--border)',
                          background: 'var(--background)',
                        }}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: 'var(--space-2) var(--space-3)',
                borderTop: '1px solid var(--border)',
                background: '#fafbfc',
                fontSize: 10,
                color: 'var(--muted-foreground)',
              }}
            >
              <span>{cfg.scaleLabels[0]}</span>
              <span>{cfg.scaleLabels[cfg.scaleLabels.length - 1]}</span>
            </div>
          </div>
        );
      })}
      <div
        style={{
          fontSize: 10,
          color: 'var(--muted-foreground)',
          marginTop: 4,
        }}
      >
        {t('answerDisplay.batterySummary', {
          items: totalItems,
          scale: cfg.scaleLabels.length,
        })}
      </div>
    </div>
  );
}

interface AnswerDisplayProps {
  question: Question;
  jumpDisplayNumByQid: Map<string, string>;
}

export function AnswerDisplay({ question, jumpDisplayNumByQid }: AnswerDisplayProps) {
  return (
    <div style={{ padding: '0 var(--space-6) var(--space-2)' }}>
      {(() => {
        switch (question.answerType) {
          case 'TEXT':
            return question.textConfig ? <TextConfigDisplay cfg={question.textConfig} /> : null;
          case 'SLIDER':
            return question.sliderConfig ? (
              <SliderConfigDisplay cfg={question.sliderConfig} />
            ) : null;
          case 'PHOTO':
            return question.photoConfig ? (
              <PhotoConfigDisplay cfg={question.photoConfig} />
            ) : null;
          case 'BATTERY':
            return question.batteryConfig ? (
              <BatteryDisplay cfg={question.batteryConfig} />
            ) : null;
          case 'SHORT_ANSWER':
            return (
              <ChoiceTable
                choices={question.choices ?? []}
                showBranch
                showCorrect={false}
                jumpDisplayNumByQid={jumpDisplayNumByQid}
              />
            );
          case 'MULTI_CHOICE':
            return (
              <ChoiceTable
                choices={question.choices ?? []}
                showBranch={false}
                showCorrect={false}
                jumpDisplayNumByQid={jumpDisplayNumByQid}
                isMultiChoice
              />
            );
          case 'YES_NO':
            return (
              <ChoiceTable
                choices={question.choices ?? []}
                showBranch
                showCorrect={false}
                jumpDisplayNumByQid={jumpDisplayNumByQid}
              />
            );
          case 'OX':
            return (
              <ChoiceTable
                choices={question.choices ?? []}
                showBranch={false}
                showCorrect
                jumpDisplayNumByQid={jumpDisplayNumByQid}
              />
            );
          default:
            return null;
        }
      })()}
    </div>
  );
}
